import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { IconAlertCircle } from '@tabler/icons-react';

const PlanManagementCard = ({
  billingDetails,
  subscriptionDetails,
  subscriptionActive,
  isSuperAdmin,
  onChangePlan,
  onChangeFrequency,
  onPauseSubscription,
}) => {
  // Helper function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);

    if (isNaN(date.getTime())) return 'Invalid Date';

    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
    } catch (e) {
      return date.toDateString();
    }
  };

  const planName = billingDetails?.plan_name || 'Solo';
  const interval = billingDetails?.interval || 'year';
  const billingCycle = interval ? `${interval.charAt(0).toUpperCase() + interval.slice(1)}ly` : 'Yearly';
  const subscriptionStatus = subscriptionActive ? 'Active' : 'Inactive';
  const nextPaymentDate = formatDate(billingDetails?.plan_valid_upto);
  const discount = billingDetails?.discount;
  const hasActiveDiscount = discount && discount.valid;

  // Helper to calculate days remaining until expiration
  const getDaysRemaining = (timestamp) => {
    if (!timestamp) return 0;
    const expirationDate = new Date(timestamp);
    const today = new Date();
    const diffTime = expirationDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Helper to calculate and format billing amount with discount
  const getBillingAmountDisplay = () => {
    if (!subscriptionDetails?.amount) return null;
    
    // For paused subscriptions, check if access has expired
    if (!subscriptionActive && billingDetails?.plan_valid_upto) {
      // For paused subscriptions, access expires at trial_end (if in trial) or immediately (if not)
      // plan_valid_upto represents the billing period end, not access expiration
      // If subscription is paused and inactive, access has already expired
      // Don't show the warning message
      return null;
    }
    
    // For active subscriptions, show billing amount
    const amount = subscriptionDetails.amount / 100;
    const currency = subscriptionDetails.currency === 'usd' ? '$' : subscriptionDetails.currency?.toUpperCase();
    const intervalText = interval === 'month' ? 'month' : 'year';
    
    let displayPrice = `${currency}${amount.toFixed(0)}`;
    let originalPrice = null;
    
    // Calculate discounted price if applicable
    if (hasActiveDiscount && discount) {
      const { discount_type, discount_value } = discount;
      let discountedAmount = subscriptionDetails.amount;
      
      if (discount_type === 'percent') {
        discountedAmount = subscriptionDetails.amount * (1 - discount_value / 100);
      } else {
        discountedAmount = subscriptionDetails.amount - discount_value;
      }
      
      originalPrice = displayPrice;
      displayPrice = `${currency}${(discountedAmount / 100).toFixed(0)}`;
    }
    
    return { 
      isPaused: false,
      originalPrice, 
      displayPrice, 
      intervalText 
    };
  };

  const billingAmountInfo = getBillingAmountDisplay();

  // Helper to format discount display
  const getDiscountDisplay = () => {
    if (!hasActiveDiscount) return null;
    
    const { discount_type, discount_value, duration, duration_in_months } = discount;
    
    let amount;
    if (discount_type === 'percent') {
      amount = `${discount_value}%`;
    } else {
      amount = `$${(discount_value / 100).toFixed(0)}`;
    }

    let durationText = '';
    if (duration === 'repeating' && duration_in_months) {
      durationText = ` for ${duration_in_months} month${duration_in_months > 1 ? 's' : ''}`;
    } else if (duration === 'forever') {
      durationText = ' ongoing';
    }

    return `${amount} off${durationText}`;
  };

  const renderButton = (label, onClick, disabled = false) => {
    if (disabled) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`tooltip-${label}`}>
              <strong>
                This action is disabled as the subscription is not active.
              </strong>
            </Tooltip>
          }
        >
          <span>
            <button className="btn-outlined" disabled={true}>
              {label}
            </button>
          </span>
        </OverlayTrigger>
      );
    }

    return (
      <button className="btn-outlined" onClick={onClick}>
        {label}
      </button>
    );
  };

  return (
    <div className="plan-management-card">
      <div className="section-header">
        <h3>Plan Management</h3>
        <p>Update your subscription preferences</p>
      </div>

      <div className="plan-management-grid">
        {/* Current Plan */}
        <div className="plan-field">
          <div className="plan-field-left">
            <label>Current Plan</label>
            <p className="plan-value">
              {planName}
              {hasActiveDiscount && (
                <span style={{
                  marginLeft: '8px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#22C55E',
                  backgroundColor: '#22C55E15',
                  padding: '2px 8px',
                  borderRadius: '4px'
                }}>
                  {getDiscountDisplay()}
                </span>
              )}
            </p>
          </div>
          <div className="plan-field-right">
            {renderButton('Change Plan', onChangePlan, !subscriptionActive)}
          </div>
        </div>

        {/* Billing Cycle */}
        <div className="plan-field">
          <div className="plan-field-left">
            <label>Billing Cycle</label>
            <p className="plan-value capitalize">{billingCycle}</p>
            {billingAmountInfo && !billingAmountInfo.isPaused && nextPaymentDate && (
              // Active subscription billing info
              <p style={{
                fontSize: '13px',
                color: '#6B7280',
                marginTop: '6px',
                marginBottom: '0',
                lineHeight: '1.4'
              }}>
                {billingAmountInfo.originalPrice && (
                  <span style={{
                    textDecoration: 'line-through',
                    marginRight: '6px',
                    color: '#9CA3AF'
                  }}>
                    {billingAmountInfo.originalPrice}
                  </span>
                )}
                <strong style={{ color: '#111827' }}>
                  {billingAmountInfo.displayPrice}
                </strong>
                <span>/{billingAmountInfo.intervalText}</span>
                <span style={{ margin: '0 6px', color: '#D1D5DB' }}>•</span>
                <span>Next billing: {nextPaymentDate}</span>
              </p>
            )}
          </div>
          <div className="plan-field-right">
            {renderButton(
              'Change Frequency',
              onChangeFrequency,
              !subscriptionActive,
            )}
          </div>
        </div>

        {/* Subscription Status */}
        <div className="plan-field">
          <div className="plan-field-left">
            <label>Subscription Status</label>
            <p className={`plan-value ${subscriptionActive ? 'active' : ''}`}>
              {subscriptionStatus}
            </p>
            {billingAmountInfo && billingAmountInfo.isPaused && (
              // Paused subscription warning
              <div style={{
                fontSize: '13px',
                backgroundColor: '#FEF3C7',
                border: '1px solid #F59E0B',
                borderRadius: '6px',
                padding: '10px 12px',
                marginTop: '8px',
                marginBottom: '0',
                width: '50%',
                lineHeight: '1.5'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IconAlertCircle size={18} style={{ color: '#F59E0B', flexShrink: 0 }} />
                  <div>
                    <div style={{ color: '#92400E', fontWeight: '600', fontSize: '13px' }}>
                      Access expires in {billingAmountInfo.daysRemaining} {billingAmountInfo.daysRemaining === 1 ? 'day' : 'days'} on {billingAmountInfo.expirationDate}
                    </div>
                    <div style={{ color: '#B45309', fontSize: '12px', marginTop: '3px' }}>
                      Resume subscription to continue service
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="plan-field-right">
            {subscriptionActive && isSuperAdmin ? (
              // Only super admins can pause active subscriptions
              <button className="btn-outlined" onClick={onPauseSubscription}>
                Pause Subscription
              </button>
            ) : !subscriptionActive ? (
              // All users can resume inactive subscriptions
              <button className="btn-outlined" onClick={onPauseSubscription}>
                Resume Subscription
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanManagementCard;
